import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAppContext } from '@prisjakt/ui-app-context';
import { useSelector } from 'react-redux';
import { AppState } from '../../../../reducers';
import { trackPriceListFilterUpdate } from '../tracking';
import {
  FilterSelection,
  FilterType,
  FilterTypeEnum,
  FilterUIPlacementEnum,
  IFilter,
  PriceDisplayEnum,
} from 'src/components/price-list/types';
import { getFiltersConfig } from 'src/components/price-list/filters-config';
import { TranslateFunc } from 'components/translate/src/translate-func';
import { IOfferListFilterContext, ContextProps } from './types';

const initialContextValue: IOfferListFilterContext = {
  selectedFilters: [],
  selectedCondition: null,
  setSelectedFilters: () => undefined,
  getActiveFilters: () => [],
  getAdditiveFilters: () => [],
  getFilter: () => undefined,
  getTranslatedLabel: () => '',
  getFilterByPlacement: () => [],
  getPriceDisplay: () => PriceDisplayEnum.PRICE,
  toggleFilter: () => {},
  setFilterSelection: () => {},
  resetFilters: () => {},
  resetFiltersByPlacement: () => {},
  isAnyFilterToggledOnByUser: () => false,
};

const OfferListFilterContext = createContext<IOfferListFilterContext>(
  initialContextValue
);

export const useOfferListFilterContext = () =>
  useContext(OfferListFilterContext);

export const OfferListFilterContextContainer = ({ children }: ContextProps) => {
  const { market } = useAppContext();
  const [filters, setFilters] = useState<IFilter[]>(getFiltersConfig());
  const selectedCondition = useSelector(
    (state: AppState) => state.areas.product.condition
  );

  useEffect(() => {
    initFilters();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getActiveFilters = () => {
    return filters.filter(filter => filter.active);
  };

  const getAdditiveFilters = () => {
    return filters.filter(filter => filter.additive);
  };

  const getFilter = (filterType: FilterType) => {
    return filters.find(filter => filter.type === filterType);
  };

  const getFilterByPlacement = (placement: FilterUIPlacementEnum) => {
    return filters.filter(filter => filter.placement === placement);
  };

  const getPriceDisplay = () => {
    return getFilter(FilterTypeEnum.PRICES_INC_SHIPPING)?.active
      ? PriceDisplayEnum.PRICE_INCLUDES_SHIPPING
      : PriceDisplayEnum.PRICE;
  };

  const getTranslatedLabel = (t: TranslateFunc, type: FilterType) => {
    const filterConfig = getFiltersConfig().find(
      filter => type === filter.type
    );
    let prefix: string;
    switch (filterConfig?.placement) {
      case FilterUIPlacementEnum.OFFERLIST_HEADER:
        prefix = 'common';
        break;
      default:
        prefix = 'product';
        break;
    }
    // @ts-ignore
    return filterConfig?.label ? (t(prefix, filterConfig.label) as string) : '';
  };

  const setDefaultActiveFilters = (
    filtersList: IFilter[],
    type: FilterType[]
  ) => {
    return filtersList.map(filter => {
      const newFilter = { ...filter };
      if (type.includes(filter.type)) {
        newFilter.active = true;
      }
      return newFilter;
    });
  };

  const setDefaultHiddenFilters = (
    filtersList: IFilter[],
    type: FilterType[]
  ) => {
    return filtersList.map(filter => {
      const updatedFilter = { ...filter };
      if (!type.includes(filter.type)) {
        updatedFilter.hidden = true;
      }

      return updatedFilter;
    });
  };

  const setFilterSelection = (
    filterType: FilterType,
    selection: FilterSelection
  ) => {
    const updatedFilters = filters.map(filter => {
      if (filterType === filter.type) {
        return { ...filter, selection, active: Boolean(selection?.length) };
      }
      return filter;
    });
    setFilters(updatedFilters);
    trackPriceListFilterUpdate(filterType, true);
  };

  const toggleFilter = (filterType: FilterType) => {
    const val = filters.find(filter => filter.type === filterType)?.active;
    const updatedFilters = filters.map(filter => {
      if (filterType === filter.type) {
        return { ...filter, active: !val };
      }
      return filter;
    });
    setFilters(updatedFilters);
    trackPriceListFilterUpdate(filterType, !val);
  };

  const resetFiltersByPlacement = (filterPlacement: FilterUIPlacementEnum) => {
    const updatedFilters = filters.map(filter => {
      if (filter.placement === filterPlacement) {
        let activeState = false;
        if (filter.additive) {
          activeState = true;
        }
        return {
          ...filter,
          active: activeState,
        };
      }
      return filter;
    });
    setFilters(updatedFilters);
  };

  const initFilters = () => {
    const initialFilters = getFiltersConfig();
    const defaultFilters =
      market.offerList?.filters?.defaultActiveFilters ?? [];
    const hiddenFilters = market.offerList?.filters?.hiddenFilters ?? [];
    let updatedFilters = setDefaultActiveFilters(
      initialFilters,
      defaultFilters
    );
    updatedFilters = setDefaultHiddenFilters(updatedFilters, hiddenFilters);

    const searchParams: URLSearchParams = new URLSearchParams(
      window.location.search
    );
    const filtersFromQuery: any[] =
      searchParams.get('filters')?.split(',') || [];

    const filtersFromQueryString = updatedFilters.map(filter => {
      const isInQueryString = filtersFromQuery.includes(filter.type);
      return { ...filter, active: isInQueryString || filter.active };
    });

    setFilters(filtersFromQueryString);
  };

  const resetFilters = () => {
    const initialFilters = getFiltersConfig();
    const hiddenFilters = market.offerList?.filters?.hiddenFilters ?? [];
    const updatedFilters = setDefaultHiddenFilters(
      initialFilters,
      hiddenFilters
    );
    setFilters(updatedFilters);
  };

  const isAnyFilterToggledOnByUser = () => {
    const appliedNormalFilters = filters.filter(f => !f.additive && f.active);
    const appliedAdditiveFilters = getAdditiveFilters().filter(
      f => f.additive && !f.active
    );
    return appliedNormalFilters.length > 0 || appliedAdditiveFilters.length > 0;
  };

  return (
    <OfferListFilterContext.Provider
      value={{
        selectedFilters: filters,
        selectedCondition,
        setSelectedFilters: setFilters,
        getActiveFilters,
        getAdditiveFilters,
        getFilter,
        getTranslatedLabel,
        getFilterByPlacement,
        getPriceDisplay,
        toggleFilter,
        setFilterSelection,
        resetFilters,
        resetFiltersByPlacement,
        isAnyFilterToggledOnByUser,
      }}
    >
      {children}
    </OfferListFilterContext.Provider>
  );
};
